import ProductRec from '../../components/ProductRec';
import { constructorParamsBuilder, togglePlaceholder } from '../../core/ConstructorSdk';

const POD_ID_ATTRIBUTE = 'data-cnstrc-recommendations-pod-id';

/**
 * "Recently Viewed" carousel on the Cart page, backed by its own Constructor pod (cart-2).
 *
 * It fetches and renders through the same pieces the shared Recommendations orchestrator uses —
 * constructorParamsBuilder, the Constructor SDK, ProductRec and togglePlaceholder — but owns its own
 * request so the sibling recommendation carousel keeps its original cart-1 / empty-cart-1 swap
 * behaviour, driven entirely by refreshCart.js, with nothing new queued alongside it.
 *
 * Visibility follows the cart's two states: shown only while the cart has items.
 */
class RecentlyViewed {
  constructor(section) {
    this.section = section;
    this.carousel = null;
  }

  async init() {
    const container = this.section.querySelector(`[${POD_ID_ATTRIBUTE}]`);
    const podId = container?.getAttribute(POD_ID_ATTRIBUTE);

    if (!podId || !window.cnstrcSdk) return this.#remove();

    const { results, resultId, numResults } = await this.#fetchRecommendations(podId, container);
    if (!results.length) return this.#remove();

    this.carousel = new ProductRec({
      gallery: this.section,
      prefixClassName: 'carousel-c-scroll',
      gap: 8,
      recData: results,
      resultId,
      numResults,
      podId,
    });
    this.carousel.init();

    togglePlaceholder(this.section);

    document.addEventListener('cartDidUpdate', (event) => this.#onCartUpdate(event.detail));
  }

  /**
   * Fetch the pod, seeding it from the products on the page the same way the shared orchestrator
   * does so both cart carousels send Constructor an identical request shape.
   * @param {String} podId - Constructor pod id
   * @param {HTMLElement} container - element carrying the Constructor recommendation attributes
   * @returns {Promise<Object>} results, resultId and numResults from the response
   */
  async #fetchRecommendations(podId, container) {
    const empty = { results: [], resultId: '', numResults: 0 };

    const productElements = document.querySelectorAll('[data-product-id]:not([data-cnstrc-item="recommendation"])');
    const itemIds = [
      ...new Set(
        Array.from(productElements)
          .map((el) => el.getAttribute('data-product-id'))
          .filter((id) => id),
      ),
    ];

    itemIds.length && container.setAttribute('data-cnstrc-recommendations-seed-items', itemIds.join(','));

    const params = itemIds.length > 0 ? { itemIds } : {};
    constructorParamsBuilder(params, true);

    try {
      const response = await window.cnstrcSdk.recommendations.getRecommendations(podId, params);
      const results = response?.response?.results || [];
      return {
        results,
        resultId: response?.result_id || '',
        numResults: response?.total_num_results || response?.response?.total_num_results || results.length,
      };
    } catch (error) {
      console.error(`Constructor Error for ${podId}: ${error}`);
      return empty;
    }
  }

  #onCartUpdate(cart) {
    // Hidden rather than removed while the cart is empty, so adding an item back brings it straight
    // up without another request.
    const isEmpty = cart?.item_count === 0;
    this.section.dataset.cartEmpty = isEmpty ? 'true' : 'false';

    // A carousel built while the section was hidden measured 0x0, so its arrows were left disabled.
    // Now that it has real dimensions, ask for that state again.
    if (!isEmpty) this.carousel?.toggleArrows();
  }

  #remove() {
    this.section.remove();
  }
}

export default () => {
  const section = document.querySelector('.recently-viewed-l');
  if (!section) return;

  new RecentlyViewed(section).init();
};
